import { useCallback, useRef, useState } from 'react'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { PackagingMinimisationAssessmentStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { useExportPackagingMinimisationAssessmentDocumentPdf } from '@/shared/hooks/api/packagingSuppliers'
import { buildPackagingMinimisationAssessmentDocumentSections } from './buildPackagingMinimisationAssessmentDocumentSections'
import { buildPackagingItemMinimisationAssessmentPdfCoverMarkdown } from './buildPackagingItemMinimisationAssessmentPdfCoverMarkdown'
import {
  RecyclabilityAssessmentDocumentEditor,
  type RecyclabilityAssessmentDocumentEditorHandle,
} from './RecyclabilityAssessmentDocumentEditor'

type PackagingItemMinimisationAssessmentDocumentSheetProps = {
  supplierId: string
  itemId: string
  itemName: string
  itemDescription: string
  intendedUse: string
  packagingIdentificationNumber: string
  manufacturerName: string
  manufacturerAddress: string
  history: PackagingMinimisationAssessmentStoredEntry[] | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PackagingItemMinimisationAssessmentDocumentSheet({
  supplierId,
  itemId,
  itemName,
  itemDescription,
  intendedUse,
  packagingIdentificationNumber,
  manufacturerName,
  manufacturerAddress,
  history,
  open,
  onOpenChange,
}: PackagingItemMinimisationAssessmentDocumentSheetProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const editorRef = useRef<RecyclabilityAssessmentDocumentEditorHandle>(null)
  const [sections, setSections] = useState<ComplianceDocumentSectionRecord[]>(
    []
  )
  const [coverMarkdown, setCoverMarkdown] = useState<string | null>(null)
  const [contentSyncKey, setContentSyncKey] = useState(0)
  const [coverContentSyncKey, setCoverContentSyncKey] = useState(0)
  const exportPdfMutation = useExportPackagingMinimisationAssessmentDocumentPdf(
    supplierId,
    itemId
  )

  const hasBuiltDocument = sections.length > 0
  const latestEntry = history?.[0]

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setSections([])
      setCoverMarkdown(null)
      setContentSyncKey(0)
      setCoverContentSyncKey(0)
      exportPdfMutation.reset()
    }
  }

  const runDocumentBuild = useCallback(() => {
    if (latestEntry == null) {
      return
    }
    const nextCoverMarkdown =
      buildPackagingItemMinimisationAssessmentPdfCoverMarkdown({
        translate: t,
        locale: i18n.language,
        itemName,
        itemDescription,
        intendedUse,
        packagingIdentificationNumber,
        manufacturerName: manufacturerName ?? '',
        manufacturerAddress: manufacturerAddress ?? '',
        reportReferenceNo: packagingIdentificationNumber.trim(),
        dateOfAssessmentIso: latestEntry.timestamp,
      })
    const nextSections = buildPackagingMinimisationAssessmentDocumentSections({
      locale: i18n.language,
      historyEntry: latestEntry,
    })
    setCoverMarkdown(nextCoverMarkdown)
    setSections(nextSections)
    setContentSyncKey((key) => key + 1)
    setCoverContentSyncKey((key) => key + 1)
  }, [
    i18n.language,
    itemDescription,
    intendedUse,
    itemName,
    latestEntry,
    manufacturerAddress,
    manufacturerName,
    packagingIdentificationNumber,
    t,
  ])

  const handleExportPdf = useCallback(async () => {
    if (editorRef.current == null) {
      return
    }
    const flushedSections = editorRef.current.flushSections()
    const flushedCover = editorRef.current.flushCoverMarkdown()
    const slug = itemName.trim().replace(/\s+/g, '-').slice(0, 80)
    try {
      await exportPdfMutation.mutateAsync({
        sections: flushedSections,
        coverMarkdown: flushedCover ?? undefined,
        documentName:
          slug.length > 0
            ? `packaging-minimisation-assessment-${slug}.pdf`
            : undefined,
      })
    } catch {
      // mutation isError drives messaging
    }
  }, [exportPdfMutation, itemName])

  const isExportPending = exportPdfMutation.isPending

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl'
      >
        <SheetHeader className='shrink-0 border-b px-6 py-4 pe-10 text-start'>
          <SheetTitle>
            {t('itemDetail.minimisation.document.sheetTitle')}
          </SheetTitle>
          <SheetDescription>
            {t('itemDetail.minimisation.document.sheetDescription')}
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
          {!hasBuiltDocument && latestEntry == null ? (
            <p className='text-sm text-muted-foreground'>
              {t('itemDetail.minimisation.document.emptyHistoryHint')}
            </p>
          ) : null}
          {hasBuiltDocument ? (
            <RecyclabilityAssessmentDocumentEditor
              ref={editorRef}
              sections={sections}
              contentSyncKey={contentSyncKey}
              coverMarkdown={coverMarkdown}
              coverContentSyncKey={coverContentSyncKey}
            />
          ) : null}
          {hasBuiltDocument && exportPdfMutation.isError ? (
            <p className='mt-4 text-sm text-destructive'>
              {t('itemDetail.minimisation.document.exportPdfFailed')}
            </p>
          ) : null}
        </div>

        <SheetFooter className='shrink-0 gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end'>
          {!hasBuiltDocument ? (
            <Button
              type='button'
              variant='outline'
              disabled={latestEntry == null}
              onClick={runDocumentBuild}
            >
              {t('itemDetail.minimisation.document.buildDocument')}
            </Button>
          ) : (
            <Button
              type='button'
              variant='outline'
              disabled={isExportPending}
              onClick={() => {
                void handleExportPdf()
              }}
            >
              {isExportPending
                ? t('itemDetail.minimisation.document.exportingPdf')
                : t('itemDetail.minimisation.document.exportPdf')}
            </Button>
          )}
          <Button
            type='button'
            onClick={() => {
              handleOpenChange(false)
            }}
          >
            {t('common:actions.close')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
